import React, { useState, useEffect } from 'react';
import { ChartBarIcon, CurrencyDollarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  const fetchAnalytics = async (start = null, end = null) => {
    try {
      setLoading(true);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;

      const data = await adminAPI.getAnalytics(params);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(dateRange.startDate, dateRange.endDate);
  }, []);

  const handleDateRangeChange = (field, value) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);
  };

  const applyDateFilter = () => {
    fetchAnalytics(dateRange.startDate, dateRange.endDate);
  };

  const resetDateFilter = () => {
    const defaultRange = {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    };
    setDateRange(defaultRange);
    fetchAnalytics(defaultRange.startDate, defaultRange.endDate);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Impossible de charger les données d'analyse</p>
      </div>
    );
  }

  const metrics = [
    {
      name: 'Revenus période',
      value: `${analytics.metrics.period_revenue.toFixed(2)} MAD`,
      period: `${analytics.metrics.start_date} - ${analytics.metrics.end_date}`
    },
    {
      name: 'Réservations période',
      value: analytics.metrics.period_bookings.toString(),
      period: `${analytics.metrics.start_date} - ${analytics.metrics.end_date}`
    },
    {
      name: 'Taux d\'annulation',
      value: `${analytics.metrics.cancellation_rate}%`,
      period: `${analytics.metrics.start_date} - ${analytics.metrics.end_date}`
    },
    {
      name: 'Taux d\'occupation',
      value: `${analytics.metrics.occupancy_rate}%`,
      period: 'Global'
    }
  ];

  const topServices = analytics.top_services || [];
  const topCoaches = analytics.top_coaches || [];
  const revenueData = analytics.charts?.revenue_evolution || [];
  const locationData = analytics.charts?.location_distribution || [];

  // Colors for charts
  const COLORS = ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a', '#059669'];

  // Custom tooltip for revenue chart
  const RevenueTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{`Date: ${label}`}</p>
          <p className="text-sm text-primary-600">
            {`Revenus: ${payload[0].value} MAD`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for location chart
  const LocationTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{payload[0].payload.name}</p>
          <p className="text-sm text-primary-600">
            {`Réservations: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analyses</h2>
          <p className="text-gray-600">Suivez les performances de votre studio</p>
        </div>

        {/* Date Filter */}
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Du:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Au:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={applyDateFilter}
              className="px-4 py-1 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 transition-colors"
            >
              Appliquer
            </button>
            <button
              onClick={resetDateFilter}
              className="px-4 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
               
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Services */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Services les plus populaires</h3>
          <div className="space-y-4">
            {topServices.length > 0 ? topServices.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{service.name}</p>
                  <p className="text-sm text-gray-500">{service.bookings} réservations</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary-600">{service.revenue} MAD</p>
                  <p className="text-sm text-gray-500">Prix</p>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>Aucune donnée de service disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Coaches */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Coachs les plus actifs</h3>
          <div className="space-y-4">
            {topCoaches.length > 0 ? topCoaches.map((coach, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">{coach.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{coach.name}</p>
                    <p className="text-sm text-gray-500">{coach.sessions} séances</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-accent-600">{coach.specialties}</p>
                  <p className="text-sm text-gray-500">Spécialités</p>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>Aucune donnée de coach disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Évolution des revenus (7 derniers jours)</h3>
          <div className="h-64">
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tickFormatter={(value) => `${value} MAD`}
                    domain={[0, 'dataMax']}
                  />
                  <Tooltip content={<RevenueTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ fill: '#dc2626', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#dc2626', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Aucune donnée de revenus</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Répartition par studio</h3>
          <div className="h-64">
            {locationData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={locationData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="bookings"
                    label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}
                  >
                    {locationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<LocationTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Aucune donnée de studio</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-primary-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-primary-600">{analytics.totals.clients}</div>
          <div className="text-sm text-primary-600">Clients actifs</div>
        </div>
        <div className="bg-accent-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-accent-600">{analytics.metrics.occupancy_rate}%</div>
          <div className="text-sm text-accent-600">Taux d'occupation</div>
        </div>
        <div className="bg-success-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success-600">{analytics.metrics.period_bookings}</div>
          <div className="text-sm text-success-600">Réservations période</div>
        </div>
        <div className="bg-warning-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning-600">{analytics.metrics.period_revenue.toFixed(0)} MAD</div>
          <div className="text-sm text-warning-600">Revenus période</div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
